import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CheckCircle2, XCircle, Info, AlertTriangle, X } from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  toast: (type: ToastType, message: string) => void;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null);

let _nextId = 1;

// ─── Per-type config ─────────────────────────────────────────────────────────

const CONFIG: Record<ToastType, { icon: React.FC<{ className?: string }>; border: string; iconCls: string; progress: string }> = {
  success: {
    icon: ({ className }) => <CheckCircle2 className={className} />,
    border: 'border-green-500/40',
    iconCls: 'text-green-400',
    progress: 'bg-green-500',
  },
  error: {
    icon: ({ className }) => <XCircle className={className} />,
    border: 'border-red-500/40',
    iconCls: 'text-red-400',
    progress: 'bg-red-500',
  },
  info: {
    icon: ({ className }) => <Info className={className} />,
    border: 'border-blue-500/40',
    iconCls: 'text-blue-400',
    progress: 'bg-blue-500',
  },
  warning: {
    icon: ({ className }) => <AlertTriangle className={className} />,
    border: 'border-yellow-500/40',
    iconCls: 'text-yellow-400',
    progress: 'bg-yellow-500',
  },
};

const DURATION = 4500;

// ─── Single toast item ────────────────────────────────────────────────────────

const ToastEntry: React.FC<{ item: ToastItem; onRemove: (id: number) => void }> = ({ item, onRemove }) => {
  const [visible, setVisible] = useState(false);
  const cfg = CONFIG[item.type];
  const Icon = cfg.icon;

  const dismiss = useCallback(() => {
    setVisible(false);
    setTimeout(() => onRemove(item.id), 320);
  }, [item.id, onRemove]);

  useEffect(() => {
    const enterTimer = setTimeout(() => setVisible(true), 16);
    const exitTimer = setTimeout(dismiss, DURATION);
    return () => {
      clearTimeout(enterTimer);
      clearTimeout(exitTimer);
    };
  }, [dismiss]);

  return (
    <div
      className={[
        'relative flex items-start gap-3 w-80 p-4 rounded-xl border shadow-2xl',
        'bg-slate-800/95 backdrop-blur-md overflow-hidden',
        'transition-all duration-300 ease-out',
        cfg.border,
        visible ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0',
      ].join(' ')}
    >
      {/* Icon */}
      <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${cfg.iconCls}`} />

      {/* Message */}
      <p className="text-sm text-slate-100 flex-1 leading-relaxed">{item.message}</p>

      {/* Close button */}
      <button
        onClick={dismiss}
        className="flex-shrink-0 -mt-0.5 -mr-1 p-1 rounded text-slate-500 hover:text-white hover:bg-slate-700 transition"
        aria-label="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      {/* Auto-dismiss progress bar */}
      <div
        className={`absolute bottom-0 left-0 h-0.5 ${cfg.progress} opacity-50`}
        style={{ animation: `toast-shrink ${DURATION}ms linear forwards` }}
      />
    </div>
  );
};

// ─── Provider ─────────────────────────────────────────────────────────────────

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const toast = useCallback((type: ToastType, message: string) => {
    setToasts(prev => [...prev, { id: _nextId++, type, message }]);
  }, []);

  const remove = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div
        className="fixed bottom-6 right-6 z-[9999] flex flex-col-reverse gap-3 items-end pointer-events-none"
        aria-live="polite"
        aria-label="Notifications"
      >
        {toasts.map(t => (
          <div key={t.id} className="pointer-events-auto">
            <ToastEntry item={t} onRemove={remove} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx.toast;
}
